import { FormEvent, useState } from "react";
import { EmarsysResponse } from "@components/Newsletter/types/Emarsys";
import {
  Newsletter,
  NEWSLETTER_REQUESTS_RESPONSES,
} from "@components/Newsletter/types/Newsletter";
import { useGTMDispatch } from "@elgorditosalsero/react-gtm-hook";
import { newsletterRequests } from "@features/newsletter/server/Newsletter.server";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button, Input, Transition } from "@headlessui/react";
import { cn } from "@util/helpers";

import DefaultSuccessMessage from "./DefaultSuccessMessage";

export type NewsletterFormProps = {
  /** The class name of the component. */
  className?: string;
  /** List of newsletters. */
  newsletters: Newsletter[];
  /** The success message/component to display when user successfully signed up. */
  successMessage?: string | React.ReactNode;
  /** The icon to display in the sign up button. */
  signUpButtonIcon?: React.ReactNode;
  /** The class name of the sign up button. */
  buttonClassName?: string;
  /** The class name of the form. */
  formClassName?: string;
  /** The class name of the input field. */
  inputClassName?: string;
  /** Whether the form should have a border below it. */
  isBordered?: boolean;
  /** Placeholder text for the input */
  placeholder?: string;
  /** Callback after clicking sign up button */
  onSubmitCallback?: () => void;
  /** Callback after successful sign up */
  onSuccessCallback?: () => void;
  /** Additional label to display below the input field */
  additionalLabel?: string | React.ReactNode;
  /** The button class name of the loading state. */
  buttonLoadingClass?: string;
  /** The class name of the error state. */
  errorClassName?: string;
  /** The class name of the form focus state. */
  formFocusClassName?: string;
};

/** The newsletter sign up form, which contains input email and a submit button. */
export default function NewsletterForm({
  className,
  newsletters,
  successMessage = <DefaultSuccessMessage />,
  signUpButtonIcon,
  buttonClassName,
  formClassName,
  inputClassName,
  isBordered = true,
  placeholder,
  onSubmitCallback,
  onSuccessCallback,
  additionalLabel,
  buttonLoadingClass,
  errorClassName,
  formFocusClassName,
}: NewsletterFormProps): React.ReactElement {
  const sendDataToGTM = useGTMDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [emarsysResponse, setEmarsysResponse] = useState<EmarsysResponse>();

  const hasError = emarsysResponse?.status === "error";

  const defaultGAOnsubmit = () => {
    newsletters.forEach((newsletter) => {
      sendDataToGTM({
        event: "custom_event",
        eventCategory: "newsletter",
        eventAction: "click",
        eventLabel: newsletter.id,
      });
    });
  };

  const defaultGAOnsuccess = () => {
    newsletters.forEach((newsletter) => {
      sendDataToGTM({
        event: "custom_event",
        eventCategory: "newsletter",
        eventAction: "signup",
        eventLabel: newsletter.id,
      });
    });
  };

  const handleGAOnsubmit = onSubmitCallback || defaultGAOnsubmit;
  const handleGAOnsuccess = onSuccessCallback || defaultGAOnsuccess;

  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    // Display the loading icon.
    setIsLoading(true);

    handleGAOnsubmit();

    // Get the value of email input field.
    const form = event.currentTarget;
    const formData = new FormData(form);
    const email = formData.get("email");

    // If by somehow the email value is empty.
    // Return an error.
    if (!email || typeof email !== "string") {
      setEmarsysResponse(NEWSLETTER_REQUESTS_RESPONSES.EMAILISREQUIRED);
      setIsLoading(false);
      return;
    }

    // Request to emarsys.
    const request = async () => {
      await newsletterRequests
        .processSubscription(email, newsletters)
        .then((response) => {
          setEmarsysResponse(response);

          if (response.status === "error") return;

          handleGAOnsuccess();
        })
        .finally(() => {
          setIsLoading(false);
        });
    };

    // Call the request.
    request();
  };

  return (
    <div data-testid="newsletter-form-component" className={className}>
      <Transition show={emarsysResponse?.status === "success"}>
        <div
          className="ease py-2 transition duration-300 data-[closed]:translate-y-9 data-[closed]:opacity-0"
          data-testid="success-message-component"
        >
          {successMessage}
        </div>
      </Transition>

      {emarsysResponse?.status !== "success" ? (
        <>
          <form
            onSubmit={onSubmit}
            className={cn(
              "relative flex flex-nowrap items-center gap-4 bg-white pb-1",
              formClassName,
              { "border-red": hasError },
              formFocusClassName
            )}
          >
            <Input
              aria-label="Email"
              placeholder={placeholder ? placeholder : "Your email address"}
              name="email"
              type="email"
              className={cn(
                "peer w-full py-2 font-poppins text-xs font-normal leading-7 outline-none placeholder:text-gray-515 autofill:bg-white",
                inputClassName
              )}
              required
              autoComplete="off"
            />
            <Button
              type="submit"
              className={cn(
                "text-nowrap font-poppins text-xs font-semibold leading-normal tracking-tightest text-gray-850",
                "w-[103px] rounded-full border border-gray-175 bg-white px-6 py-2 text-center transition-colors",
                "hover:border-gray-850",
                buttonClassName,
                { [buttonLoadingClass || ""]: isLoading }
              )}
              data-testid="newsletter-sign-up-button"
            >
              {isLoading ? (
                <FontAwesomeIcon
                  icon={faSpinner}
                  size="lg"
                  color="text-white"
                  spin
                />
              ) : (
                <>{signUpButtonIcon ? signUpButtonIcon : "Sign up"}</>
              )}
            </Button>

            {isBordered ? (
              <div
                className={cn(
                  "absolute top-full w-full border-b border-gray-350 transition-colors peer-focus:border-gray-850",
                  { "border-red": hasError }
                )}
              ></div>
            ) : null}
          </form>

          {!hasError ? additionalLabel : null}
        </>
      ) : null}

      {hasError ? (
        <p
          className={cn(
            "py-2 font-poppins text-xs font-medium leading-normal text-red",
            errorClassName
          )}
        >
          {emarsysResponse.message}
        </p>
      ) : null}
    </div>
  );
}
